/*
 *   Copyright 2022-2024 SenX S.A.S.
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { h, Host } from "@stencil/core";
import { DataModel } from "../../model/types";
import { Param } from "../../model/param";
import { Logger } from "../../utils/logger";
import { GTSLib } from "../../utils/gts.lib";
import { Utils } from "../../utils/utils";
import html2canvas from "html2canvas";
import { isEqual } from "lodash";
export class DiscoverySvgComponent {
    constructor() {
        this.defOptions = new Param();
        this.funqueue = [];
        this.refs = [];
        this.result = undefined;
        this.type = undefined;
        this.start = undefined;
        this.options = new Param();
        this.width = undefined;
        this.height = undefined;
        this.debug = false;
        this.unit = '';
        this.url = undefined;
        this.chartTitle = undefined;
        this.execTime = 0;
        this.bgColor = undefined;
        this.fontColor = undefined;
        this.parsing = false;
        this.toDisplay = [];
        this.innerStyle = undefined;
        this.innerResult = undefined;
        this.innerOptions = undefined;
    }
    updateRes() {
        this.innerResult = GTSLib.getData(this.result);
        this.parseResult();
    }
    optionsUpdate(newValue, oldValue) {
        var _a, _b;
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['optionsUpdate'], newValue, oldValue);
        let opts = newValue;
        if (!!newValue && typeof newValue === 'string') {
            opts = JSON.parse(newValue);
        }
        if (!isEqual(opts, this.innerOptions)) {
            this.innerOptions = Object.assign({}, opts);
            setTimeout(() => this.parseResult());
            (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['optionsUpdate 2'], { options: this.innerOptions, newValue, oldValue });
        }
    }
    discoveryEventHandler(event) {
        this.funqueue.push(this.wrapFunction(this.processEvent.bind(this), this, [event]));
    }
    async resize() {
        const dims = Utils.getContentBounds(this.el.parentElement);
        this.width = dims.w;
        this.height = dims.h;
        return Promise.resolve();
    }
    componentWillLoad() {
        const dims = Utils.getContentBounds(this.el.parentElement);
        this.width = dims.w;
        this.height = dims.h;
        this.parseResult();
        this.processQueue();
    }
    convert(data) {
        var _a, _b;
        const toDisplay = [];
        this.refs = [];
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['convert'], data);
        let options = Utils.mergeDeep(this.defOptions, this.innerOptions || {});
        options = Utils.mergeDeep(options || {}, data.globalParams);
        this.innerOptions = Object.assign({}, options);
        if (this.innerOptions.customStyles) {
            this.innerStyle = Object.assign(Object.assign({}, this.innerStyle), this.innerOptions.customStyles || {});
        }
        if (GTSLib.isArray(data.data)) {
            (data.data || []).forEach(img => {
                var _a;
                (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['convert'], DiscoverySvgComponent.isSVG(img));
                if (DiscoverySvgComponent.isSVG(img)) {
                    toDisplay.push(this.sanitize(img));
                }
            });
        }
        else if (data.data && DiscoverySvgComponent.isSVG(data.data)) {
            (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['convert'], DiscoverySvgComponent.isSVG(data.data));
            toDisplay.push(this.sanitize(data.data));
        }
        return toDisplay;
    }
    processEvent(event) {
        return new Promise(resolve => {
            const res = Utils.parseEventData(event.detail, this.innerOptions.eventHandler, this.el.id);
            if (res.style) {
                this.innerStyle = Object.assign(Object.assign({}, this.innerStyle), res.style);
            }
            if (res.xpath) {
                const toDisplay = [];
                (this.toDisplay || []).forEach(img => {
                    var _a;
                    (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['convert'], DiscoverySvgComponent.isSVG(img));
                    if (DiscoverySvgComponent.isSVG(img)) {
                        toDisplay.push(this.sanitize(img, res.xpath.selector, res.xpath.value));
                    }
                });
                this.toDisplay = [...toDisplay];
            }
            resolve(true);
        });
    }
    wrapFunction(fn, context, params) {
        return () => fn.apply(context, params);
    }
    processQueue() {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        void new Promise(async (resolve) => {
            while (this.funqueue.length > 0) {
                await (this.funqueue.shift())();
            }
            resolve(true);
        }).then(() => setTimeout(() => this.processQueue(), 100));
    }
    parseResult() {
        var _a;
        this.parsing = true;
        this.LOG = new Logger(DiscoverySvgComponent, this.debug);
        if (typeof this.options === 'string') {
            this.innerOptions = JSON.parse(this.options);
        }
        else {
            this.innerOptions = this.options;
        }
        this.innerResult = GTSLib.getData(this.result);
        this.toDisplay = this.convert(this.innerResult || new DataModel());
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['componentWillLoad'], {
            type: this.type,
            options: this.innerOptions,
            toDisplay: this.toDisplay,
        });
        setTimeout(() => {
            this.refs.forEach(svgWrapper => {
                var _a;
                (((_a = this.innerOptions.svg) === null || _a === void 0 ? void 0 : _a.handlers) || []).forEach(h => {
                    if (!!h.selector) {
                        svgWrapper.querySelectorAll(h.selector).forEach(elem => {
                            elem.classList.add('hoverable');
                            if (!!h.click) {
                                elem.addEventListener('click', () => this.triggerEvent(h.event));
                            }
                            if (!!h.hover) {
                                elem.addEventListener('mouseover', () => this.triggerEvent(h.event));
                            }
                        });
                    }
                });
            });
        });
        this.parsing = false;
        this.draw.emit();
    }
    static isSVG(data) {
        return typeof data === 'string' && /<svg/gi.test(data);
    }
    sanitize(svg, xpath, replacement) {
        var _a;
        try {
            const svgDoc = Utils.parseXML(svg, 'image/svg+xml');
            const el = svgDoc.getElementsByTagName('svg').item(0);
            if (!!xpath) {
                const nsXpath = xpath.split('/').filter(e => !!e).map(e => 'svg:' + e).join('/');
                const iterator = svgDoc.evaluate(nsXpath, svgDoc, prefix => prefix === 'svg' ? 'http://www.w3.org/2000/svg' : null, XPathResult.ORDERED_NODE_ITERATOR_TYPE, null);
                let elem = iterator.iterateNext();
                const elemsToReplace = [];
                while (elem) {
                    elemsToReplace.push(elem);
                    elem = iterator.iterateNext();
                }
                elemsToReplace.forEach(e => {
                    if (typeof replacement === 'string') {
                        const parent = e.parentElement;
                        const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
                        g.innerHTML = replacement.trim();
                        parent.replaceChild(g.firstChild, e);
                    }
                    else {
                        Object.keys(replacement).forEach(k => e.setAttribute(k, replacement[k].toString()));
                    }
                });
            }
            if (el.getAttribute('width') && el.getAttribute('height')) {
                el.setAttribute('viewBox', '0 0 '
                    + el.getAttribute('width').replace(/[a-z]+/gi, '') + ' '
                    + el.getAttribute('height').replace(/[a-z]+/gi, ''));
            }
            if (el.getAttribute('preserveAspectRatio')) {
                el.removeAttribute('preserveAspectRatio');
            }
            el.setAttribute('preserveAspectRatio', 'xMinYMin meet');
            return new XMLSerializer().serializeToString(svgDoc);
        }
        catch (e) {
            (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.error(['exec'], e);
            return svg;
        }
    }
    triggerEvent(evt) {
        this.discoveryEvent.emit(Object.assign(Object.assign({}, evt), { source: this.el.id }));
    }
    async export(type = 'png') {
        return type === 'svg' ? this.toDisplay : (await html2canvas(this.el)).toDataURL();
    }
    generateStyle(innerStyle) {
        return Object.keys(innerStyle || {}).map(k => k + ' { ' + innerStyle[k] + ' }').join('\n');
    }
    render() {
        return (h(Host, { key: '1cd639a08a5551946b13db16e9b513f043456a34' }, h("style", { key: '09de60652fcfcbb40c8f4fc1423816530704b8e5' }, this.generateStyle(this.innerStyle)), h("div", { key: '46d09a2de0a3e612c9a457e4983b2680b91cc32d', class: "svg-wrapper", style: { width: `${this.width}px`, height: `${this.height}px` } }, this.parsing
            ? h("discovery-spinner", null, "Parsing data...")
            : this.toDisplay.length > 0
                ? this.toDisplay.map(svg => h("div", { class: "svg-container", innerHTML: svg, ref: el => this.refs.push(el) }))
                : '')));
    }
    static get is() { return "discovery-svg"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["discovery-svg.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["discovery-svg.css"]
        };
    }
    static get properties() {
        return {
            "result": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "DataModel | string",
                    "resolved": "DataModel | string",
                    "references": {
                        "DataModel": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DataModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "result",
                "reflect": false
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChartType",
                    "resolved": "\"line\" | \"area\" | \"scatter\" | \"step-area\" | \"spline-area\" | \"spline\" | \"step\" | \"step-after\" | \"step-before\" | \"annotation\" | \"bar\" | \"bar-polar\" | \"display\" | \"image\" | \"map\" | \"gauge\" | \"linear-gauge\" | \"circle\" | \"compass\" | \"pie\" | \"doughnut\" | \"rose\" | \"tabular\" | \"svg\" | \"input:text\" | \"input:list\" | \"input:secret\" | \"input:autocomplete\" | \"input:chips\" | \"input:chips-autocomplete\" | \"input:slider\" | \"input:date\" | \"input:date-range\" | \"input:multi\" | \"input:multi-cb\" | \"button\" | \"button:radio\" | \"hidden\" | \"calendar\" | \"heatmap\" | \"profile\" | \"boxplot\" | \"dashboard\" | \"dashboard:flex\" | \"dashboard:scada\"",
                    "references": {
                        "ChartType": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::ChartType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "type",
                "reflect": false
            },
            "start": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "start",
                "reflect": false
            },
            "options": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "Param | string",
                    "resolved": "Param | string",
                    "references": {
                        "Param": {
                            "location": "import",
                            "path": "../../model/param",
                            "id": "src/model/param.ts::Param"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "options",
                "reflect": false,
                "defaultValue": "new Param()"
            },
            "width": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "width",
                "reflect": false
            },
            "height": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "height",
                "reflect": false
            },
            "debug": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "debug",
                "reflect": false,
                "defaultValue": "false"
            },
            "unit": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "unit",
                "reflect": false,
                "defaultValue": "''"
            },
            "url": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "url",
                "reflect": false
            },
            "chartTitle": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "chart-title",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "execTime": {},
            "bgColor": {},
            "fontColor": {},
            "parsing": {},
            "toDisplay": {},
            "innerStyle": {},
            "innerResult": {},
            "innerOptions": {}
        };
    }
    static get events() {
        return [{
                "method": "draw",
                "name": "draw",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "discoveryEvent",
                "name": "discoveryEvent",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "DiscoveryEvent",
                    "resolved": "DiscoveryEvent",
                    "references": {
                        "DiscoveryEvent": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DiscoveryEvent"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "resize": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "export": {
                "complexType": {
                    "signature": "(type?: 'png' | 'svg') => Promise<string | string[]>",
                    "parameters": [{
                            "name": "type",
                            "type": "\"svg\" | \"png\"",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<string | string[]>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "result",
                "methodName": "updateRes"
            }, {
                "propName": "options",
                "methodName": "optionsUpdate"
            }];
    }
    static get listeners() {
        return [{
                "name": "discoveryEvent",
                "method": "discoveryEventHandler",
                "target": "window",
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=discovery-svg.js.map
